let isFetching = false;

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    // The custom element may wrap a form (sometimes not), so guard.
    const facetForm = this.querySelector('form');
    if (facetForm) {
      // Listen for both input and change so selects (sort-by) trigger updates.
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
      facetForm.addEventListener('change', this.debouncedOnSubmit.bind(this));
    }

    // Initialize infinite scroll on page load
    FacetFiltersForm.reinitializeInfiniteScroll();

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  /*
   * NEW: Re-initializes infinite scroll.
   * This is now a static method so it can be called after AJAX updates.
   */
  static reinitializeInfiniteScroll() {
    // Disconnect any previous observer to prevent memory leaks
    if (FacetFiltersForm.observer) {
      FacetFiltersForm.observer.disconnect();
    }

    const loadMoreTrigger = document.querySelector('.load-more_wrap');

    // If there's no trigger element (i.e., only one page of products), do nothing.
    if (!loadMoreTrigger) return;

    const loadMoreProducts = () => {
      // Prevent multiple fetches at the same time
      if (isFetching) return;
      isFetching = true;

      const totalPagesEl = document.querySelector('#total-pages');
      const currentPageEl = document.querySelector('#current-page');
      const nextUrlEl = document.querySelector('#next-url');

      if (!totalPagesEl || !currentPageEl || !nextUrlEl) {
        isFetching = false;
        return;
      }

      const totalPages = totalPagesEl.value;
      const currentPage = currentPageEl;
      const nextUrl = nextUrlEl.value;
      const nextPage = parseInt(currentPage.value) + 1;

      const spinner = document.querySelector('.infinite-scroll-spinner');
      if (spinner) spinner.classList.remove('hidden');

      fetch(nextUrl)
        .then((response) => response.text())
        .then((html) => {
          let parser = new DOMParser();
          let doc = parser.parseFromString(html, 'text/html');

          let newItems = doc.querySelector('.grid--view-items');
          let newLoadMoreWrap = doc.querySelector('.load-more_wrap .load-more-properties');

          if (newItems) {
            // Append new products to the grid
            const existingGrid = document.querySelector('.grid--view-items');
            if (existingGrid) existingGrid.append(...newItems.children);
          }

          if (newLoadMoreWrap) {
            // Update the hidden inputs and 'Showing X of Y' text
            const existingLoadMoreProps = document.querySelector('.load-more_wrap .load-more-properties');
            if (existingLoadMoreProps) existingLoadMoreProps.innerHTML = newLoadMoreWrap.innerHTML;
          }

          currentPage.value = nextPage;
          if (nextPage >= parseInt(totalPages)) {
            // All pages loaded, remove the trigger element and observer
            const trigger = document.querySelector('.load-more_wrap');
            if (trigger) trigger.remove();
            if (FacetFiltersForm.observer) {
              FacetFiltersForm.observer.disconnect();
            }
          }
        })
        .catch((error) => console.error('Error loading more products:', error))
        .finally(() => {
          if (spinner) spinner.classList.add('hidden');
          isFetching = false;
        });
    };

    FacetFiltersForm.observer = new IntersectionObserver(
      (entries) => {
        // If the trigger element is intersecting the viewport, load more products
        if (entries[0] && entries[0].isIntersecting) {
          loadMoreProducts();
        }
      },
      { rootMargin: '0px 0px 400px 0px' } // Triggers the load 400px before the element is in view
    );

    FacetFiltersForm.observer.observe(loadMoreTrigger);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));

    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer) {
      const collection = productGridContainer.querySelector('.collection');
      if (collection) collection.classList.add('loading');
    }

    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      })
      .catch((err) => {
        console.error('Error fetching section:', err);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const newContent = parsed.getElementById('ProductGridContainer');
    if (newContent) {
      const container = document.getElementById('ProductGridContainer');
      if (container) {
        container.innerHTML = newContent.innerHTML;
        container.querySelectorAll('.scroll-trigger').forEach((element) => {
          element.classList.add('scroll-trigger--cancel');
        });
      }
    }

    // ADDED: Re-initialize infinite scroll after the product grid has been updated.
    FacetFiltersForm.reinitializeInfiniteScroll();
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const countEl = parsed.getElementById('ProductCount');
    const count = countEl ? countEl.innerHTML : '';
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter, #FacetSortFormMobile .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.classList.add('d-none');
      } else currentElement.classList.remove('d-none');
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            const prevEl = document.getElementById(previousElementId);
            if (prevEl) prevEl.after(elementToRender);
            return;
          }
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && event && event.target) {
      const jsFilterEl = event.target.closest('.js-filter');
      const closestJSFilterID = jsFilterEl ? jsFilterEl.id : null;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector =
          newFacetDetailsElement && newFacetDetailsElement.classList.contains('mobile-facets__details')
            ? `.mobile-facets__close-button`
            : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement
          ? newFacetDetailsElement.querySelector(newElementSelector)
          : null;

        const isTextInput = event.target.getAttribute && event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const target = document.querySelector(selector);
      if (target) target.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const target = document.querySelector(selector);
      if (target) target.innerHTML = html.querySelector(selector).innerHTML;
    });

    // Defensive: only call bindEvents if the element exists and supports it.
    try {
      const mobileForm = document.getElementById('FacetFiltersFormMobile');
      if (mobileForm) {
        const drawer = mobileForm.closest('menu-drawer');
        if (drawer && typeof drawer.bindEvents === 'function') drawer.bindEvents();
      }
    } catch (e) {
      // ignore if menu-drawer not present
    }
  }

  static renderCounts(source, target) {
    if (!target) return;

    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));

      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    if (!target) return;

    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    // If something else triggered this and there's no target, skip.
    if (!event || !event.target) return;

    // Prevent native form submission for safety.
    if (event.preventDefault) event.preventDefault();

    // Collect all forms inside custom elements
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');

    // Determine if the triggering form is a mobile form.
    const formTarget = event.target.closest('form') || event.currentTarget || null;
    const formId = formTarget ? formTarget.id : null;
    const isMobileTrigger = formId && /Mobile|mobile/i.test(formId);

    const forms = [];

    // Define allowed desktop sort/filter form IDs (expandable)
    const desktopAllowed = new Set(['FacetSortForm', 'FacetFiltersForm', 'FacetSortDrawerForm']);
    // Define allowed mobile form ids (expandable)
    const mobileAllowed = new Set(['FacetFiltersFormMobile', 'FacetSortFormMobile', 'FacetSortFormMobile']);

    sortFilterForms.forEach((form) => {
      const id = form.id || '';
      // If this is a mobile trigger, only include mobile forms:
      if (isMobileTrigger) {
        if (mobileAllowed.has(id) || /Mobile|mobile/i.test(id)) {
          forms.push(this.createSearchParams(form));
        }
      } else {
        // Desktop: include main filter + sort forms
        if (
          desktopAllowed.has(id) ||
          id === 'FacetFiltersForm' ||
          id === 'FacetSortForm' ||
          id === 'FacetSortDrawerForm'
        ) {
          forms.push(this.createSearchParams(form));
        }
      }
    });

    // Fallback: if no forms collected, try to submit the nearest form (ensures something sent).
    if (forms.length === 0 && formTarget) {
      forms.push(this.createSearchParams(formTarget));
    }

    this.onSubmitForm(forms.join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);

    setTimeout(() => {
      if (typeof this.updateProductHeight === 'function') {
        this.updateProductHeight();
      }
    }, 500);
  }
}

// ADDED: A static property to hold the observer instance.
FacetFiltersForm.observer = null;
FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput && maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput && minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput && minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput && maxInput.value === '') minInput.setAttribute('data-max', minInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (!isNaN(min) && value < min) input.value = min;
    if (!isNaN(max) && value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (!facetLink) return;
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (form && typeof form.onActiveFilterClick === 'function') {
      form.onActiveFilterClick(event);
    }
  }
}

customElements.define('facet-remove', FacetRemove);

/* Helper: simple debounce */
function debounce(func, wait) {
  let timeout;
  return function () {
    const context = this,
      args = arguments;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}

/* Helper placeholder for escape key handler referenced in original code */
function onKeyUpEscape(e) {
  if (e.key === 'Escape' || e.keyCode === 27) {
    // implement if required by theme
  }
}
